import {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { ethers } from 'ethers';
import { handleGetChainInfo } from '@/utils/wallet';
import { useAuth } from '@/hooks/useAuth';
import {
  getL2ChainInfo,
  getCryptoAssets,
  getAllERC20,
} from '@/resources/wallet-service.resource';
import { getBlockchainConfigurations } from '@/resources/nft-service.resource';
import { UniswapSDK } from './swap/sdk';
import { ERC20_ABI } from './swap/abi';

import {
  getEncryptedData,
  decryptMnemonic,
  decryptLegacyMnemonic,
  initializeTokenConfig,
  prepareWallet,
  updateTokenConfig,
} from '@/providers/helpers/web3Helper';
import {
  getL2Fees,
  sendBep20Transaction,
  sendBinanceTransaction,
  sendBTCTransaction,
  sendERCTransaction,
  sendETHTransaction,
  sendL2Transaction,
  sendLegacyETHTransaction,
  sendLegacyERCTransaction,
  sendLegacyBep20Transaction,
  sendLegacyBinanceTransaction,
  sendLegacyBTCTransaction,
  sendLegacyL2Transaction,
} from '@/providers/helpers/transactionHelpers';

const Web3Context = createContext(null);

// get from backend?
const NETWORKS = {
  1: {
    name: 'Mainnet',
  },
  11155111: {
    name: 'Sepolia',
  },
};

export const useWeb3 = () => {
  const context = useContext(Web3Context);
  if (!context) {
    throw new Error('useWeb3 must be used within a Web3Provider');
  }
  return context;
};

export function Web3Provider({ children }) {
  const { fetcher, status, authStatuses } = useAuth();
  const [blockChainConfig, setBlockChainConfig] = useState([]);
  const chainIdIntervalRef = useRef(null);
  const sdkRef = useRef(null);

  const getBlockchainConfig = async () => {
    const { blockchainConfigurations } = await fetcher(getBlockchainConfigurations());
    setBlockChainConfig(blockchainConfigurations);
  };

  const [error, setError] = useState(null);
  const [chainInfo, setChainInfo] = useState(null);
  const [l2ChainInfo, setL2ChainInfo] = useState(null);
  const [tokenConfig, setTokenConfig] = useState(null);
  const [networks, setNetworks] = useState(NETWORKS);
  const [erc20Configurations, setErc20Configurations] = useState([]);
  const [erc20ConfigFallback, setErc20ConfigFallback] = useState([]);

  const getTokenAddress = useCallback(
    (tokenSymbol) => {
      if (!erc20Configurations || erc20Configurations.length === 0) {
        console.error('Token configurations state:', {
          isNull: !erc20Configurations,
          length: erc20Configurations?.length || 0,
        });
        throw new Error('Token configurations not loaded yet');
      }

      if (tokenSymbol.toUpperCase() === 'ETH') {
        return UniswapSDK.NATIVE_ETH_ADDRESS;
      }

      const tokenConfig = erc20Configurations.find(
        (token) => token.canonicalName.toUpperCase() === tokenSymbol.toUpperCase()
      );

      if (!tokenConfig) {
        console.error(
          'Available tokens:',
          erc20Configurations.map((t) => ({
            symbol: t.canonicalName,
            address: t.definitionAddress,
          }))
        );
        throw new Error(
          `Token not found: ${tokenSymbol}. Available tokens: ${erc20Configurations.map((t) => t.canonicalName).join(', ')}`
        );
      }

      if (!tokenConfig.definitionAddress) {
        throw new Error(`Contract address not found for token: ${tokenSymbol}`);
      }

      return tokenConfig.definitionAddress;
    },
    [erc20Configurations]
  );

  useEffect(() => {
    if (authStatuses.SIGNED_IN !== status) {
      return;
    }
    getBlockchainConfig();
    const fetchAndSetChainId = async () => {
      if (status !== authStatuses.SIGNED_IN) {
        if (chainIdIntervalRef.current) clearInterval(chainIdIntervalRef.current);
        return;
      }

      getL2ChainData();

      const chainInfo = await handleGetChainInfo(fetcher);

      if (chainInfo?.chainId) {
        setChainInfo(chainInfo);
        setTokenConfig(initializeTokenConfig(chainInfo.chainId)); // Use actual chainId instead of initChainId
        // After getting chainInfo, fetch ERC20 configs
        try {
          const cryptoAssets = await fetcher(getCryptoAssets());
          setErc20Configurations(cryptoAssets);
        } catch (err) {
          console.error('Failed to fetch Crypto Assets:', err);
        }
        try {
          const erc20 = await fetcher(getAllERC20());
          const updatedConfig = updateTokenConfig(
            initializeTokenConfig(chainInfo.chainId),
            erc20.erc20Configurations
          );
          setTokenConfig(updatedConfig);
          setErc20ConfigFallback(erc20.erc20Configurations);
        } catch (err) {
          console.error('Failed to fetch ERC20 tokens:', err);
        }
      }
    };

    if (chainInfo?.chainId) {
      // Set initial placeholder values
      setTokenConfig(initializeTokenConfig(chainInfo.chainId)); // Use actual chainId

      // Immediately fetch ERC20 configs to update the placeholders
      fetcher(getCryptoAssets())
        .then((data) => {
          const updatedConfig = updateTokenConfig(
            initializeTokenConfig(chainInfo.chainId), // Use actual chainId
            data.erc20Configurations
          );
          setTokenConfig(updatedConfig);
          setErc20Configurations(data.erc20Configurations);
        })
        .catch((err) => {
          console.error('Failed to fetch ERC20 tokens:', err);
        });

      return;
    } else {
      fetchAndSetChainId();
    }
  }, [status, authStatuses.SIGNED_IN, fetcher]);

  useEffect(() => {
    // If crypto assets table not populated, create the data needed
    if (
      !erc20Configurations.length &&
      blockChainConfig.length &&
      erc20ConfigFallback.length
    ) {
      const cryptoData = erc20ConfigFallback.map((token, i) => ({
        id: token.id,
        canonicalName: token.symbol,
        displayName: `${token.name} (ERC20)`,
        assetType: {
          id: i,
          name: 'Erc20',
        },
        definitionAddress: token.contractAddress,
        decimalPrecision: token.decimalPrecision,
        blockchainConfiguration: {
          chainId: token.chainId,
          name: token.name,
          rpcUrl: blockChainConfig.find((config) => config.chainId === token.chainId)
            .rpcUrl,
          blockExplorerUrl: blockChainConfig.find(
            (config) => config.chainId === token.chainId
          ).blockExplorerUrl,
          usesEIP1559: blockChainConfig.find((config) => config.chainId === token.chainId)
            .usesEIP1559,
          isDefaultLayer1: blockChainConfig.find(
            (config) => config.chainId === token.chainId
          ).isDefaultLayer1,
          isDefaultLayer2: blockChainConfig.find(
            (config) => config.chainId === token.chainId
          ).isDefaultLayer2,
        },
        settings: {
          allowPurchases: token.allowPurchases,
          allowInApp: token.allowInApp,
          allowSell: token.allowSell,
        },
      }));
      setErc20Configurations(cryptoData);
    }
  }, [erc20Configurations, blockChainConfig, erc20ConfigFallback]);

  useEffect(() => {
    if (blockChainConfig.length) {
      // If not sepolia or mainnet, update network data from db
      blockChainConfig.forEach((blockchain) => {
        if (blockchain.chainId !== 1 && blockchain.chainId !== 11155111) {
          setNetworks((prevNetworks) => ({
            ...prevNetworks,
            [blockchain.chainId]: {
              name: blockchain.name,
              rpcUrl: blockchain.rpcUrl,
            },
          }));
        }
      });
    }
  }, [blockChainConfig]);

  const provider = useMemo(() => {
    if (!chainInfo) return null;
    try {
      const chainId = chainInfo?.chainId;
      const network = networks[chainId];

      if (!network?.name) {
        throw new Error(`Unsupported chain ID: ${chainId}`);
      }
      // using nextjs server side api call to protect key
      const url = `${window.location.origin}/api/infura`;
      const provider = new ethers.providers.JsonRpcProvider(url, Number(chainId));

      // Initialize UniswapSDK
      if (!sdkRef.current) {
        sdkRef.current = new UniswapSDK({
          jsonRpcUrl: url,
          chainId: Number(chainId),
          debug: process.env.NODE_ENV === 'development',
        });
        sdkRef.current.init().catch(console.error);
      }

      return provider;
    } catch (err) {
      setError('Failed to initialize provider');
      console.error('Provider initialization error:', err);
      return null;
    }
  }, [chainInfo?.chainId, networks]);

  const bep20Provider = useMemo(() => {
    const bepCoin = blockChainConfig.find((token) => token.name.includes('BSC'));
    if (bepCoin) {
      try {
        const url = `${window.location.origin}/api/coins/bep20Infura`;
        const bepProvider = new ethers.providers.JsonRpcProvider(url, bepCoin.chainId);
        return bepProvider;
      } catch (err) {
        setError('Failed to initialize BEP20 Provider');
        console.error('BEP20 Provider initialization error:', err);
        return null;
      }
    }
  }, [blockChainConfig.length]);

  const getL2ChainData = async () =>
    await fetcher(getL2ChainInfo())
      .then((data) => {
        setL2ChainInfo(data);
      })
      .catch(() => console.error('Error fetching L2 chain data'));

  async function getBalance(address) {
    try {
      if (!provider) throw new Error('Provider not initialized');
      const balance = await provider.getBalance(address);
      return ethers.formatEther(balance);
    } catch (err) {
      setError('Failed to get balance');
      console.error('Balance fetch error:', err);
      throw err;
    }
  }

  async function getTokenBalance(tokenAddress, walletAddress) {
    try {
      if (!provider) throw new Error('Provider not initialized');
      const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
      const balance = await tokenContract.balanceOf(walletAddress);
      const decimals = await tokenContract.decimals();

      return ethers.utils.formatUnits(balance, decimals);
    } catch (err) {
      setError('Failed to get token balance');
      console.error('Token balance fetch error:', err);
      throw err;
    }
  }

  async function getLatestBlock() {
    try {
      if (!provider) throw new Error('Provider not initialized');
      return await provider.getBlock('latest');
    } catch (err) {
      setError('Failed to get latest block');
      console.error('Block fetch error:', err);
      throw err;
    }
  }

  async function bridgePublicToPrivate({
    passcode,
    amountBN,
    sourceProvider,
    destinationProvider,
    sourceAssetLockContractAddress,
    tokenAddress,
    releaseFee,
    destinationAssetReleaseContractAddress,
    decimals,
  }) {
    try {
      const sourceWallet = await prepareWallet(fetcher, passcode, sourceProvider);
      const destinationWallet = await prepareWallet(
        fetcher,
        passcode,
        destinationProvider
      );
      const tokenContract = new ethers.Contract(
        tokenAddress,
        ['function approve(address spender, uint256 amount) external returns (bool)'],
        sourceWallet
      );
      const publicBridgeContract = new ethers.Contract(
        sourceAssetLockContractAddress,
        ['function lockTokens(uint256 amount, uint8 sourceChain) external'],
        sourceWallet
      );
      const privateBridgeContract = new ethers.Contract(
        destinationAssetReleaseContractAddress,
        ['function payReleaseFee() external payable'],
        destinationWallet
      );

      const releaseFeeBN = ethers.utils.parseUnits(releaseFee.toString(), decimals);

      await privateBridgeContract.payReleaseFee({ value: releaseFeeBN });

      const approveTx = await tokenContract.approve(
        sourceAssetLockContractAddress,
        amountBN
      );
      await approveTx.wait();

      const lockTx = await publicBridgeContract.lockTokens(amountBN, 0, {
        gasLimit: 500000n,
      });

      const receipt = await lockTx.wait();
      return receipt;
    } catch (err) {
      console.error('Transaction signing error:', err);
      throw err;
    }
  }

  async function bridgePrivateToPublic({
    passcode,
    amountBN,
    sourceProvider,
    destinationProvider,
    sourceAssetLockContractAddress,
    releaseFee,
    destinationAssetReleaseContractAddress,
  }) {
    try {
      const sourceWallet = await prepareWallet(fetcher, passcode, sourceProvider);
      const destinationWallet = await prepareWallet(
        fetcher,
        passcode,
        destinationProvider
      );

      const publicContract = new ethers.Contract(
        destinationAssetReleaseContractAddress,
        ['function payReleaseFee() external payable'],
        destinationWallet
      );

      // Let's try awaiting this and see if it takes to long
      await publicContract.payReleaseFee({ value: releaseFee, gasLimit: 300000n });

      const privateBridgeContract = new ethers.Contract(
        sourceAssetLockContractAddress,
        ['function lockTokens(uint8) external payable'],
        sourceWallet
      );

      const enumForChain = 1; // 1 for eth, 2 for binance This needs to be from somewhere, it is not the chain id will be added to bridgexconfig table
      const tx = await privateBridgeContract.lockTokens(enumForChain, {
        value: amountBN,
        gasLimit: 500000n,
      });

      const receipt = await tx.wait();

      return receipt;
    } catch (err) {
      console.error('Transaction signing error:', err);
      throw err;
    }
  }

  async function signTransaction(passcode, to, value) {
    try {
      const encryptedData = await getEncryptedData(fetcher);

      if (!encryptedData) throw new Error('No encrypted data available');

      let mnemonic = null;
      let wallet = null;
      let signedTx = null;

      try {
        mnemonic = decryptMnemonic(encryptedData, passcode);
        wallet = ethers.Wallet.fromMnemonic(mnemonic);
        const connectedWallet = wallet.connect(provider);
        const latestNonce = await provider.getTransactionCount(wallet.address, 'latest');

        const tx = {
          to,
          value: ethers.utils.parseEther(value.toString()),
          nonce: latestNonce,
          gasLimit: ethers.utils.hexlify(100000),
          gasPrice: await provider.getGasPrice(),
        };

        signedTx = await connectedWallet.signTransaction(tx);

        return signedTx;
        // if you are ready to send transaction on signing instead of return signedTx we can use const txResponse = await provider.sendTransaction(signedTx);
      } finally {
        // Clear sensitive data from memory
        if (mnemonic) mnemonic = null;
        wallet = null;
      }
    } catch (err) {
      console.error('Transaction signing error:', err);
      throw err;
    }
  }

  async function sendWeb3Transaction({
    passcode,
    from,
    to,
    amount,
    l2Send = false,
    symbol,
    decimals,
    isMaxSend,
    bep20Send,
    isBinance,
    isLegacy,
  }) {
    if (isLegacy) {
      const privateKey = await decryptLegacyMnemonic(from, passcode, fetcher); // Returns private key for legacy wallets

      // Get network for BTC transactions
      const network = await provider.getNetwork();
      if (!network) return;

      let response = null;
      if (symbol === 'ETH') {
        response = await sendLegacyETHTransaction({
          to,
          value: amount,
          provider,
          privateKey,
          chainId: chainInfo?.chainId,
          isMaxSend,
        });
      } else if (symbol === 'BTC') {
        response = await sendLegacyBTCTransaction({
          to,
          value: amount,
          privateKey,
          network,
          from,
          isMaxSend,
        });
      } else if (l2Send) {
        response = await sendLegacyL2Transaction({
          to,
          value: amount,
          privateKey,
          l2ChainInfo,
          isMaxSend,
        });
      } else if (bep20Send) {
        const bep20Info = erc20Configurations.find(
          (token) =>
            token.displayName.includes('BEP20') &&
            token.settings.allowInApp &&
            token.canonicalName === symbol
        );
        if (!bep20Info) return null;
        response = await sendLegacyBep20Transaction({
          to,
          value: amount,
          privateKey,
          bep20Info,
          isMaxSend,
          bep20Provider,
        });
      } else if (isBinance) {
        const binanceInfo = erc20Configurations.find(
          (token) => token.displayName.includes('Binance') && token.settings.allowInApp
        );
        if (!binanceInfo) return null;
        response = await sendLegacyBinanceTransaction({
          to,
          value: amount,
          privateKey,
          isMaxSend,
          binanceInfo,
          bep20Provider,
        });
      } else {
        response = await sendLegacyERCTransaction({
          to,
          value: amount,
          provider,
          privateKey,
          chainId: chainInfo?.chainId,
          tokenAddress: tokenConfig[chainInfo?.chainId]?.tokens[symbol]?.address,
          decimals,
          isMaxSend,
        });
      }
      return response;
    } else {
      const network = await provider.getNetwork();
      if (!network) return;
      const encryptedData = await getEncryptedData(fetcher);
      let mnemonic = '';

      mnemonic = decryptMnemonic(encryptedData, passcode);
      let response = null;
      if (symbol === 'ETH') {
        response = await sendETHTransaction({
          to,
          value: amount,
          provider,
          mnemonic,
          chainId: chainInfo?.chainId,
          isMaxSend,
        });
        mnemonic = '';
      } else if (symbol === 'BTC') {
        response = await sendBTCTransaction({
          to: to,
          value: amount,
          mnemonic,
          network,
          from: from,
          isMaxSend,
        });
        mnemonic = '';
      } else if (l2Send) {
        response = await sendL2Transaction({
          to,
          value: amount,
          mnemonic,
          l2ChainInfo,
          isMaxSend,
        });
        mnemonic = '';
      } else if (bep20Send) {
        const bep20Info = erc20Configurations.find(
          (token) =>
            token.displayName.includes('BEP20') &&
            token.settings.allowInApp &&
            token.canonicalName === symbol
        );
        if (!bep20Info) return null;
        response = await sendBep20Transaction({
          to,
          value: amount,
          mnemonic,
          bep20Info,
          isMaxSend,
          bep20Provider,
        });
        mnemonic = '';
      } else if (isBinance) {
        const binanceInfo = erc20Configurations.find(
          (token) => token.displayName.includes('Binance') && token.settings.allowInApp
        );
        if (!binanceInfo) return null;
        response = await sendBinanceTransaction({
          to,
          value: amount,
          mnemonic,
          isMaxSend,
          binanceInfo,
          bep20Provider,
        });
        mnemonic = '';
      } else {
        response = await sendERCTransaction({
          to,
          value: amount,
          provider,
          mnemonic,
          chainId: chainInfo?.chainId,
          tokenAddress: tokenConfig[chainInfo?.chainId]?.tokens[symbol]?.address,
          decimals,
          isMaxSend,
        });
        mnemonic = '';
      }
      return response;
    }
  }

  const getL2GasFees = async ({ amount, to, walletAddress }) => {
    try {
      const fees = await getL2Fees({ amount, to, walletAddress, l2ChainInfo });
      return fees;
    } catch (err) {
      console.error('Failed to get L2 gas fees:', err);
      throw err;
    }
  };

  const getBinanceGasFees = async ({ amount, to }) => {
    try {
      const value = ethers.utils.parseEther(amount.toString());
      const gasLimit = await provider.estimateGas({
        to,
        value,
      });

      // Retrieve the current gas price from the network
      const gasPrice = await provider.getGasPrice();

      // Calculate the total transaction fee
      const feeInWei = gasLimit.mul(gasPrice);
      const feeInBNB = ethers.utils.formatEther(feeInWei);

      return {
        coin: feeInBNB,
        gasFeeLoading: false,
      };
    } catch (err) {
      console.error('Failed to get Binance gas fees:', err);
      throw err;
    }
  };

  const getSwapQuote = useCallback(
    async (fromToken, toToken, amount) => {
      try {
        if (!provider) throw new Error('Provider not initialized');
        if (!tokenConfig) throw new Error('Token config not initialized');
        if (!sdkRef.current) throw new Error('SDK not initialized');
        if (!erc20Configurations || erc20Configurations.length === 0) {
          console.error('Token configurations missing:', {
            isNull: !erc20Configurations,
            length: erc20Configurations?.length || 0,
            chainId: chainInfo?.chainId,
          });
          throw new Error('Token configurations not loaded yet');
        }

        // Get token addresses first, regardless of network
        const fromTokenAddress = getTokenAddress(fromToken);
        const toTokenAddress = getTokenAddress(toToken);

        const quote = await sdkRef.current.getQuote({
          fromTokenAddress,
          toTokenAddress,
          amountIn: amount.toString(),
          slippagePercentage: 0.5, // Default 0.5% slippage
        });

        if (!quote) throw new Error('Failed to get quote');

        // Match the exact field names from the quote response
        const result = {
          protocol: quote.protocol,
          estimatedAmountOut: quote.estimatedAmountOut,
          amountOutMinimum: quote.amountOutMinimum,
          estimatedGasAmount: quote.estimatedGasAmount,
          route: quote.route || [],
          fromTokenAddress,
          toTokenAddress,
        };

        console.error('Returning quote:', result);
        return result;
      } catch (err) {
        console.error('Failed to get swap quote:', err);
        throw err;
      }
    },
    [provider, tokenConfig, chainInfo, getTokenAddress, erc20Configurations]
  );

  async function executeSwap({ fromToken, toToken, amount, slippage = 0.5, wallet }) {
    if (!provider) throw new Error('Provider not initialized');
    if (!tokenConfig) throw new Error('Token config not initialized');
    if (!wallet) throw new Error('Wallet not provided');
    if (!sdkRef.current) throw new Error('Universal Router SDK not initialized');

    try {
      const fromTokenAddress = getTokenAddress(fromToken);
      const toTokenAddress = getTokenAddress(toToken);

      const swapResult = await sdkRef.current.swap({
        mnemonic: wallet.mnemonic.phrase,
        fromTokenAddress,
        toTokenAddress,
        amountIn: amount.toString(),
        slippagePercentage: slippage,
        chainId: chainInfo.chainId,
      });

      // If we got a successful result with hash and amountOut
      if (swapResult && swapResult.hash) {
        return {
          success: true,
          hash: swapResult.hash,
          receipt: null, // Receipt will be available after confirmation
          amountIn: amount.toString(),
          amountOut: swapResult.amountOut,
          estimatedGas: '0',
          status: 'pending',
        };
      }

      throw new Error('Swap failed: Invalid response from SDK');
    } catch (err) {
      throw new Error(`Swap failed: ${err.reason || err.message}`);
    }
  }

  async function getWalletFromPasscode(passcode) {
    const encryptedData = await getEncryptedData(fetcher);
    if (!encryptedData) throw new Error('No encrypted data available');

    try {
      const mnemonic = decryptMnemonic(encryptedData, passcode);

      const wallet = ethers.Wallet.fromMnemonic(mnemonic);

      return wallet;
    } catch (err) {
      throw new Error('Invalid passcode: ', +err.message);
    }
  }

  async function getBscGasFees({ amount, to, walletAddress, tokenAddress }) {
    try {
      if (!bep20Provider) throw new Error('BSC provider not initialized');

      const gasPrice = await bep20Provider.getGasPrice();
      let estimatedGasLimit;

      if (tokenAddress) {
        // BEP20 token transfer
        const tokenContract = new ethers.Contract(
          tokenAddress,
          ['function transfer(address to, uint256 value) returns (bool)'],
          bep20Provider
        );
        const amountBN = ethers.utils.parseUnits(amount.toString(), 18);
        estimatedGasLimit = await tokenContract.estimateGas.transfer(to, amountBN, {
          from: walletAddress,
        });
      } else {
        // Native BNB transfer
        const amountBN = ethers.utils.parseUnits(amount.toString(), 18);
        estimatedGasLimit = await bep20Provider.estimateGas({
          to,
          value: amountBN,
          from: walletAddress,
        });
      }

      // Add 20% buffer to gas limit
      estimatedGasLimit = estimatedGasLimit.mul(120).div(100);

      const gasFee = estimatedGasLimit.mul(gasPrice);
      const formattedFee = ethers.utils.formatUnits(gasFee, 18);

      return {
        coin: formattedFee,
        gasFeeLoading: false,
      };
    } catch (err) {
      if (err?.message?.includes('insufficient funds')) {
        const error = new Error('Insufficient funds');
        error.ErrorCode = 60004;
        throw error;
      }
      console.error('Failed to estimate BSC gas fees:', err);
      throw new Error('Failed to estimate transaction fees');
    }
  }

  const value = {
    bep20Provider,
    chainInfo,
    erc20Configurations,
    erc20ConfigFallback,
    error,
    executeSwap,
    getBalance,
    getBinanceGasFees,
    getL2GasFees,
    getBscGasFees,
    getSwapQuote,
    getTokenBalance,
    getLatestBlock,
    getWalletFromPasscode,
    l2ChainInfo,
    provider,
    sendWeb3Transaction,
    bridgePrivateToPublic,
    bridgePublicToPrivate,
    signTransaction,
    tokenConfig,
  };

  return <Web3Context.Provider value={value}>{children}</Web3Context.Provider>;
}
